import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { EdpModalService } from "@edp-components/edp-modal";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { NgbModalOptions, NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { EdpContractThirdPartyComponent } from "@edp-components/edp-contract-third-party/edp-contract-third-party.component";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getPendingContracts, getContract, getUrlToRedirect, filteredContracts } from "@edp-store/selectors/contract.selectors";
import { Contract, IFilteredContractList } from "@edp-components/edp-contract/edp-contract.model";
import {
    SelectContract,
    GetContracts,
    GetPendingContracts,
    HideContractSelection,
    GetContractFilters,
} from "@edp-store/actions/contract.actions";
import { ContractStatusEnum } from "@edp-enums/contract-status-enum";
import { EdpSelectList } from "@edp-models/edp-select-list";
import { ContractService } from "@edp-services/contract.service";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { TranslateService } from "@ngx-translate/core";
import { EdpResponseBody } from "@edp-models/response.model";
import { Router } from "@angular/router";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { PendingContract, RemovePendingContract } from "@edp-models/edp-pending-contract.model";
import { AuthenticationService } from "@edp-services/authentication.service";
import { AppBannerService } from "@edp-services/app-banner.service";
import { DeepLinkService } from "@edp-services/deep-link.service";
import { getUser } from "@edp-store/selectors/user.selectors";
import { ModalConfirmChangesComponent } from "@edp-pages/contract-consumption-local/modal-confirm-changes/modal-contract-confirm-changes.component";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { ModalOrigin } from "@edp-models/modal.model";
import { GetBenefitsUsersDetails } from "@edp-store/actions/benefits.actions";

const keyRouteRedirectLocalStorage = "redirectToRoute";

export interface ContractManagerArgs {
    account_id: string;
    user_id: string;
    nif: string;
    firstName: string;
    lastName: string;
}
@Component({
    selector: "edp-contract-list",
    templateUrl: "./edp-contract-list.component.html",
    styleUrls: ["./edp-contract-list.component.scss"],
})
export class EdpContractListComponent implements OnInit, OnDestroy {
    public contracts: Contract[];
    public tempContracts: Contract[];
    public ContractStatusEnum = ContractStatusEnum;
    public selectedContract: Contract;
    public hasManagedContracts: boolean;
    public hasPendingContractsWithToken: boolean;
    @Input() public data: IModalData;
    public isCollapsed = true;
    public contractsManagedByMe: EdpSelectList[] = [];
    public contractsManagedByOthers: EdpSelectList[] = [];
    public contractsPendingList: EdpSelectList[] = [];
    public multipleContractsName: string = null;
    public showMultipleContractsWarning: boolean;
    public activeTab: number;
    public pendingContracts: PendingContract[];
    public disableRemoveButton = true;
    public selectListItemSelected: EdpSelectList;
    public isSearching = false;
    public isSearchable = false;
    public searchByText: string;
    public accessCode: string;
    public showAppBanner = false;
    public isLoading = false;
    public firebaseId: string;

    private unsubscribe = new Subject();

    public cameFromNewLocal = false;

    public EDIT_ACCESS_SEULOCAL = "edit_access_seulocal";
    public EDIT_REMOVE_ACCESS_SEULOCAL = "edit_remove_access_seulocal";
    public SELECT_CONTRACT_START_ADD3OS = "select_contract_start_add3os";

    constructor(
        private store: Store<IAppState>,
        private edpModalService: EdpModalService,
        private contractService: ContractService,
        private translate: TranslateService,
        private toaster: EdpToasterService,
        private router: Router,
        private authService: AuthenticationService,
        private readonly appBannerService: AppBannerService,
        private deeplinkService: DeepLinkService,
        private readonly operationHistoryService: OperationHistoryService
    ) {
        this.showAppBanner = this.appBannerService.show();
        this.store.pipe(select(filteredContracts), takeUntil(this.unsubscribe)).subscribe((contracts: IFilteredContractList) => {
            if (contracts) {
                this.isSearchable = contracts.total > 1;
                this.contracts = this.sortContracts(contracts.filtered);
                this.tempContracts = this.contracts;
                this.hasManagedContracts = contracts.filtered.some(ct => ct.IsManaged);
                if (this.isLoading) {
                    this.isLoading = false;
                    this.store.dispatch(new ShowLoader(false));
                }
            }
        });
        this.store.pipe(select(getContract), takeUntil(this.unsubscribe)).subscribe((contract: Contract) => {
            this.selectedContract = contract;
        });
        this.store.pipe(select(getPendingContracts), takeUntil(this.unsubscribe)).subscribe((pendingContracts: PendingContract[]) => {
            if (pendingContracts) {
                this.pendingContracts = pendingContracts;
                this.hasPendingContractsWithToken = pendingContracts.some(contract => contract.source === "sms");

                this.contractsPendingList = [];
                pendingContracts.forEach(contract => {
                    const selectList: EdpSelectList = new EdpSelectList(contract.nif, contract.name.toUpperCase(), false, null, contract);
                    this.contractsPendingList.push(selectList);
                });
            }
        });
        this.store.pipe(select(getUser)).subscribe(user => {
            if (user) {
                this.firebaseId = user.uid;
            }
        });
    }

    ngOnInit(): void {
        this.fillContractsSelectedLists();
        this.searchByText = "";

        if (!!this.data?.collection?.newLocal) {
            this.cameFromNewLocal = true;
        }

        if (this.tempContracts.length === 0) {
            this.registerOperationEmptyContracts();
        }
    }

    public dismiss(): void {
        this.edpModalService.close(false);
    }

    private navigateAndCheckQueryParams(url: string): void {
        this.router.navigate([url], { queryParamsHandling: "merge" });
    }

    public selectContract(contract: Contract): void {
        if (localStorage.getItem("local-deeplink")) {
            this.store.dispatch(new SelectContract(contract));
            this.deeplinkService.setNewLocalDeeplink(localStorage.getItem("local-deeplink"), contract.ContractId);
            localStorage.removeItem("local-deeplink");
        } else {
            if (!!this.cameFromNewLocal) {
                if (this.selectedContract.ContractId !== contract.ContractId) {
                    this.selectedContract = contract;
                    this.store.dispatch(new SelectContract(contract));
                    this.router.navigate([`/locais-consumo/${this.selectedContract.ContractId}/inicio`]);
                }
            } else {
                this.store.dispatch(new SelectContract(contract));
                this.store.pipe(take(1), select(getUrlToRedirect)).subscribe(linkConfigured => {
                    if (linkConfigured && this.authService.hasAccess(linkConfigured.ruleType, contract)) {
                        if (linkConfigured.contractNeeded === "1" && !!linkConfigured.acronym?.includes("DEFAULT")) {
                            this.store.dispatch(new HideContractSelection());
                            this.navigateAndCheckQueryParams(linkConfigured.url);
                        } else if (linkConfigured.contractNeeded === "1") {
                            this.store.dispatch(new HideContractSelection());
                            this.navigateAndCheckQueryParams(`energia/${contract.ContractId}/${linkConfigured.url}`);
                        } else {
                            this.navigateAndCheckQueryParams(linkConfigured.url);
                        }
                    } else {
                        !!this.hasRouteToRedirect()
                            ? this.routeToRedirect()
                            : this.navigateAndCheckQueryParams(`/energia/${contract.ContractId}/inicio`);
                    }
                });
            }
        }

        this.dismiss();
    }

    private hasRouteToRedirect() {
        return !!localStorage.getItem(keyRouteRedirectLocalStorage);
    }

    private routeToRedirect() {
        const routeToRedirect = localStorage.getItem(keyRouteRedirectLocalStorage);
        localStorage.removeItem(keyRouteRedirectLocalStorage);
        this.router.navigate([routeToRedirect], { queryParamsHandling: "merge" });
    }

    public addContract(): void {
        this.fireOperationHistoryEvent(this.SELECT_CONTRACT_START_ADD3OS);
        const modalOptions: NgbModalOptions = {
            windowClass: "edp-dialog-fullscreen edp-dialog-inside-dialog",
            container: ".edp-contract-third-party-modal-container",
        };

        const modalData: IModalData = {
            title: "",
            showClose: true,
            origin: ModalOrigin.CONTRACT_LIST,
        };

        this.edpModalService.custom(EdpContractThirdPartyComponent, modalOptions, modalData).then(() => {
            /* Intentional resolver */
        });
    }

    public syncWallet(): void {
        this.store.dispatch(new ShowLoader(true));
        this.isLoading = true;
        this.contractService.syncWallet(this.firebaseId).subscribe(
            () => this.store.dispatch(new GetContracts(true)),
            () => this.store.dispatch(new ShowLoader(false)),
            () => this.store.dispatch(new GetBenefitsUsersDetails())
        );
    }

    public toggleEditAccess(): void {
        this.fireOperationHistoryEvent(this.EDIT_ACCESS_SEULOCAL);
        this.isCollapsed = !this.isCollapsed;
        if (!this.isCollapsed) {
            this.resetEditAccess();
        }
    }

    public removeAccess(isByMe: boolean): void {
        this.fireOperationHistoryEvent(this.EDIT_REMOVE_ACCESS_SEULOCAL);
        this.disableRemoveButton = true;

        this.contractService.removeContractManager(this.selectListItemSelected.data.info).subscribe((resp: EdpResponseBody) => {
            if (resp.Success) {
                this.toaster
                    .create(
                        this.translate.instant(isByMe ? "pt.edp.edponline.remove.place.success" : "pt.edp.edponline.remove.access.success"),
                        EdpToastType.success
                    )
                    .show();
                this.store.dispatch(new GetContracts());
                this.toggleEditAccess();
            } else {
                this.toaster
                    .create(
                        this.translate.instant(isByMe ? "pt.edp.edponline.remove.place.success" : "pt.edp.edponline.remove.access.error"),
                        EdpToastType.error
                    )
                    .show();
            }
        });
    }

    public validateContract(): void {
        if (this.accessCode && this.selectListItemSelected) {
            const contract: PendingContract = this.selectListItemSelected.data;
            const args = {
                CPE_CUI: contract.cpe,
                Nif: contract.nif,
                OwnerName: contract.name,
                Token: this.accessCode,
            };

            this.store.dispatch(new ShowLoader(true));
            this.contractService.addContract(args).subscribe(
                (resp: EdpResponseBody) => {
                    if (resp.Success) {
                        this.toaster
                            .create(this.translate.instant("pt.edp.edponline.thirdparty.accesscode.validate.message"), EdpToastType.success)
                            .show();

                        this.store.dispatch(new GetContracts());
                        this.store.dispatch(new GetPendingContracts());
                        this.toggleEditAccess();
                    } else {
                        this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error).show();
                    }

                    this.store.dispatch(new ShowLoader(false));
                },
                (response: Error) => {
                    this.toaster.create(response.message, EdpToastType.error).show();
                    this.store.dispatch(new ShowLoader(false));
                }
            );
        }
    }

    public onSelectListChange(event: EdpSelectList): void {
        this.disableRemoveButton = false;
        this.multipleContractsName = event.text;
        this.showMultipleContractsWarning = event.data.multipleContracts;
        this.selectListItemSelected = event;
    }

    public navChanged(changeEvent: NgbNavChangeEvent): void {
        this.activeTab = changeEvent.nextId; // needed to paint the close button depending on activeTab
        this.showMultipleContractsWarning = false;
        this.disableRemoveButton = true;
    }

    public showSearchContract() {
        this.isSearching = !this.isSearching;
    }

    public onKeyPress(event: KeyboardEvent) {
        if (event.key === "Enter" || this.searchByText.length >= 0 || !this.searchByText.length) {
            this.tempContracts = this.contracts.filter(contract => {
                return (
                    contract.Nickname.toLowerCase().indexOf(this.searchByText.toLowerCase()) !== -1 ||
                    contract.BasicComponent.some(
                        bscComponent => bscComponent.DeliveryPoint.toLowerCase().indexOf(this.searchByText.toLowerCase()) !== -1
                    )
                );
            });
        }
    }

    private resetEditAccess(): void {
        this.disableRemoveButton = true;
        this.fillContractsSelectedLists();
    }

    private sortContracts(contracts: Contract[]): Contract[] {
        const status = {
            [ContractStatusEnum.ACTIVE]: 1,
            [ContractStatusEnum.IN_ACTIVATION]: 2,
            [ContractStatusEnum.INACTIVE]: 3,
        };
        const sortByNickname = this.sortByNickname(contracts);
        const sortByCity = this.sortByCity(sortByNickname);

        return sortByCity.sort((a, b) => {
            if (!status[a.Status] || !status[b.Status]) {
                return 0;
            } else if (status[a.Status] < status[b.Status]) {
                return -1;
            } else if (status[a.Status] > status[b.Status]) {
                return 1;
            }
        });
    }

    private sortByNickname(contracts: Contract[]) {
        return contracts.slice().sort((a, b) => {
            if (a.Nickname > b.Nickname) {
                return 1;
            }
            if (a.Nickname < b.Nickname) {
                return -1;
            }
            return 0;
        });
    }

    private sortByCity(sortByNickname: Contract[]) {
        return sortByNickname.sort((a, b) => {
            if (a.getInstallationAddress().City > b.getInstallationAddress().City) {
                return 1;
            }
            if (a.getInstallationAddress().City < b.getInstallationAddress().City) {
                return -1;
            }
            return 0;
        });
    }

    private fillContractsSelectedLists(): void {
        this.contractsManagedByMe = [];
        this.contractsManagedByOthers = [];
        this.showMultipleContractsWarning = false;

        if (this.contracts) {
            this.contracts.forEach(contract => {
                let managerInfo: ContractManagerArgs;

                if (contract.IsManagedByMe !== undefined) {
                    managerInfo = {
                        account_id: contract.IsManagedBy.account_id,
                        user_id: contract.IsManagedBy.user_id,
                        firstName: contract.IsManagedBy.firstName,
                        lastName: contract.IsManagedBy.lastName,
                        nif: contract.IsManagedBy.nif,
                    };
                }

                if (contract.IsManagedByMe === true) {
                    // needed because undefined comes along... :()
                    this.fillContractsManagedByMe(contract, managerInfo);
                } else if (contract.IsManagedByMe === false) {
                    this.fillContractsManagedByOthers(contract, managerInfo);
                }
            });
        }
    }

    private fillContractsManagedByMe(contract: Contract, managerInfo: ContractManagerArgs) {
        let auxObj: EdpSelectList;

        if (this.filterDupsByAccountId(this.contractsManagedByMe, contract.IsManagedBy.account_id) === undefined) {
            auxObj = new EdpSelectList(contract.AccountId, `${contract.CustomerFirstName} ${contract.CustomerLastName}`.toUpperCase());

            auxObj.data = { multipleContracts: false, info: managerInfo };

            this.contractsManagedByMe.push(auxObj);
        }
    }

    private fillContractsManagedByOthers(contract: Contract, managerInfo: ContractManagerArgs) {
        let auxObj: EdpSelectList;

        if (this.filterDupsByAccountId(this.contractsManagedByOthers, contract.IsManagedBy.account_id) === undefined) {
            auxObj = new EdpSelectList(
                contract.IsManagedBy.account_id,
                `${contract.IsManagedBy.firstName} ${contract.IsManagedBy.lastName}`.toUpperCase()
            );

            auxObj.data = { multipleContracts: false, info: managerInfo };
            this.contractsManagedByOthers.push(auxObj);
        }
    }

    private filterDupsByAccountId(selectedList: EdpSelectList[], accountId: string): EdpSelectList {
        const idx: EdpSelectList = selectedList.find(item => item.data.info.account_id === accountId);

        if (idx) {
            idx.data.multipleContracts = true;
        }

        return idx;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public openNewLocalModal() {
        this.router.navigate([`/locais-consumo/${this.selectedContract.ContractId}/inicio`]);
        this.dismiss();
    }

    public openRemoveContractModal(contract: PendingContract) {
        const modalOptions: NgbModalOptions = {
            windowClass: "edp-modal-window edp-dialog-fullscreen edp-dialog-inside-dialog edp-contract-confirm-phone-modal",
            container: ".edp-contract-third-party-modal-container",
        };

        this.edpModalService.custom(ModalConfirmChangesComponent, modalOptions, {
            title: this.translate.instant("pt.edp.edponline.remove.contract.modal.title", {
                contractNickname: contract.nickName,
            }),
            message: this.translate.instant("pt.edp.edponline.remove.contract.modal.message", {
                contractNickname: contract.nickName,
            }),
            collection: {
                fn: () => {
                    this.removeContract(contract);
                },
            },
            showClose: true,
        });
    }

    public removeContract(contract: PendingContract) {
        this.store.dispatch(new ShowLoader(true));

        this.contractService.removeContract({ CPE_CUI: contract.cpe, Nif: contract.nif, OwnerName: contract.name }).subscribe(
            (resp: EdpResponseBody) => {
                if (resp.Success) {
                    this.store.dispatch(new GetPendingContracts());
                    this.edpModalService.close(false);
                } else {
                    this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error).show();
                }
                this.store.dispatch(new ShowLoader(false));
            },
            (response: Error) => {
                this.toaster.create(response.message, EdpToastType.error).show();
                this.store.dispatch(new ShowLoader(false));
            }
        );
    }

    public fireOperationHistoryEvent(event: string) {
        return this.operationHistoryService.registerWithContract(null, event);
    }

    private registerOperationEmptyContracts() {
        this.operationHistoryService.registerWithContract(null, `USER_WITHOUT_CONTRACTS_SHOWING`, null, null, null, false);
    }
}
